import { useState, useEffect, useCallback } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { Badge } from 'primereact/badge';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationItem from './notification-item';
import MemoDialog from './memo-dialog';
import { Icon } from '../common/icon';
import { notificationsRuntimeService } from '../../services/notifications.service';
import { useToast } from '../../contexts/toast-context';
import type { Notification, NotificationAction } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import { Button } from 'sharedUi/Button';
import { isNotificationUnread } from './read-state';
import { needsDialog, performAction } from './perform-action';

interface NotificationBellProps {
  contactId: string;
  /** Kept for API compatibility; realtime now uses FCM, not a WebSocket token. */
  token?: string;
}

// Module-level Set to track shown notifications across component re-renders and HMR
const globalShownNotificationIds = new Set<string>();

const NotificationBell = ({ contactId }: NotificationBellProps) => {
  const op = useRef<OverlayPanel>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Toast context for showing in-app popups
  const { addToast } = useToast();

  // In-app routing for deeplink/navigate actions.
  const navigate = useNavigate();

  // Memo dialog state
  const [memoDialogOpen, setMemoDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [selectedAction, setSelectedAction] = useState<NotificationAction | null>(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await notificationsRuntimeService.getMyNotifications({
        contactId: contactId,
        page: 1,
        pageSize: 50,
      });

      setNotifications(response.data);
      setUnreadCount(response.unreadCount ?? response.data.filter(isNotificationUnread).length);
    } catch (err: any) {
      console.error('Failed to fetch notifications:', err);
      setError(extractErrorMessage(err, 'Failed to load notifications'));
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback(
    (message: any) => {
      if (message.type === 'new_notification') {
        // New notification received
        // The socket is its own producer and has spelled the id both ways, so it
        // is normalised here, once, to the one key everything else reads.
        const newNotification = {
          ...message.data,
          _id: message.data._id || message.data.id || '',
        };

        // Check if we've already shown this notification (prevent duplicates).
        // Read the same key that is stored: this checked `id` and stored `_id`, so
        // it never matched and a repeat was always toasted again. An empty id
        // cannot identify anything, so it is neither checked nor stored.
        // Always a string: FCM data values are strings, and the fallback is ''.
        // String() pins it, since the message itself is untyped.
        const shownKey = String(newNotification._id || '');
        if (shownKey && globalShownNotificationIds.has(shownKey)) {
          return;
        }

        if (shownKey) {
          // Mark this notification as shown
          globalShownNotificationIds.add(shownKey);

          // Clear old notification IDs after 5 minutes to prevent memory leak
          setTimeout(() => {
            globalShownNotificationIds.delete(shownKey);
          }, 5 * 60 * 1000);
        }

        // Actually show the notification (only if it's new)
        setNotifications((prevNotifs) => {
          // Double-check it's not already in the list
          if (prevNotifs.some(n => n._id === newNotification._id)) {
            return prevNotifs;
          }
          return [newNotification, ...prevNotifs];
        });
        setUnreadCount((prev) => prev + 1);

        // Also show toast notification (in-app popup) for redundancy.
        // Browser/system notifications are handled centrally by push-notification.service.
        addToast(newNotification);
      } else if (message.type === 'notification_update' || message.type === 'notification_read' || message.type === 'notification_action') {
        // Update existing notification
        setNotifications((prev) =>
          prev.map((n) => (n._id === message.data.id || n._id === message.data._id ? { ...message.data, id: message.data.id || message.data._id || '' } : n))
        );

        // Recalculate unread count
        setNotifications((prev) => {
          const count = prev.filter(isNotificationUnread).length;
          setUnreadCount(count);
          return prev;
        });
      }
    },
    [addToast]
  );

  // Initialize - fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // FCM + API realtime model (no WebSocket): an FCM foreground message re-emits
  // `fcm:message` (see push-notification.service). We optimistically toast from
  // the push payload, then refetch the inbox for the authoritative state.
  useEffect(() => {
    const onFcm = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const note = detail.notification || {};
      const data = detail.data || {};
      const merged = { ...data, ...note };
      // Only the tab being looked at shows it. Firebase hands a foreground push to
      // every open tab, and a toast in a hidden one is a second copy waiting there.
      // Claiming it (preventDefault) tells push-notification.service it was shown
      // here, so it does not also raise a system notification for the same push.
      if (document.visibilityState === 'visible' && merged && (merged.notificationId || merged.title)) {
        e.preventDefault();
        handleWebSocketMessage({
          type: 'new_notification',
          data: {
            id: merged.notificationId,
            _id: merged.notificationId,
            title: merged.title,
            body: merged.body,
            priority: merged.priority,
            triggerType: merged.triggerType,
            errorCode: merged.errorCode,
            alertStage: merged.alertStage,
            createdDateTime: merged.createdDateTime || new Date().toISOString(),
          },
        });
      }
      fetchNotifications();
    };
    window.addEventListener('fcm:message', onFcm as EventListener);
    return () => window.removeEventListener('fcm:message', onFcm as EventListener);
  }, [fetchNotifications, handleWebSocketMessage]);

  const handleBellClick = (event: React.MouseEvent<HTMLElement>) => {
    op.current?.toggle(event);
  };

  // Mark single notification as read
  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationsRuntimeService.markAsRead(notificationId);

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => (n._id === notificationId ? { ...n, readAt: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      setActionLoading(true);
      await notificationsRuntimeService.markAllAsRead(contactId);

      // Update local state
      const now = new Date().toISOString();
      setNotifications((prev) => prev.map((n) => ({ ...n, readAt: n.readAt || now })));
      setUnreadCount(0);
    } catch (err: any) {
      console.error('Failed to mark all as read:', err);
      setError(extractErrorMessage(err, 'Failed to mark all as read'));
    } finally {
      setActionLoading(false);
    }
  };

  // Record the action, reflect it locally, then run its side effect.
  const recordAndApply = async (
    notification: Notification,
    action: NotificationAction,
    memo?: string
  ) => {
    try {
      setActionLoading(true);
      const actedNotificationId = notification._id;
      await notificationsRuntimeService.recordAction(actedNotificationId, {
        actionId: action.actionId,
        memo: memo || undefined,
      });

      // BE records the action and returns { success, message }; update local state directly.
      const actedAt = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === actedNotificationId
            ? { ...n, selectedAction: action.actionId, memo: memo || null, actionAt: actedAt }
            : n
        )
      );

      // Close dialog
      setMemoDialogOpen(false);
      setSelectedNotification(null);
      setSelectedAction(null);

      // Only after the record succeeds — navigating away from a failed write
      // would leave the user believing the action was taken.
      try {
        performAction(action, (to) => {
          op.current?.hide();       // leaving for another screen: close the panel
          navigate(to);
        });
      } catch (effectErr: unknown) {
        console.error('Action recorded but its effect failed:', effectErr);
        setError(extractErrorMessage(effectErr, `'${action.label}' was recorded but could not be completed.`));
      }
    } catch (err: any) {
      console.error('Failed to record action:', err);
      setError(extractErrorMessage(err, 'Failed to record action'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle action click. The dialog only when there is something to ask — a note
  // to write or a question to confirm; otherwise one click does it.
  const handleActionClick = (notification: Notification, action: NotificationAction) => {
    if (!needsDialog(action)) {
      void recordAndApply(notification, action);
      return;
    }
    setSelectedNotification(notification);
    setSelectedAction(action);
    setMemoDialogOpen(true);
  };

  // Handle memo dialog confirm
  const handleMemoConfirm = async (memo: string) => {
    if (!selectedNotification || !selectedAction) return;
    await recordAndApply(selectedNotification, selectedAction, memo);
  };

  // Handle memo dialog cancel
  const handleMemoCancel = () => {
    setMemoDialogOpen(false);
    setSelectedNotification(null);
    setSelectedAction(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleBellClick}
        className="p-2 relative text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        aria-label="notifications"
      >
        <Icon name="bell" size={20} />
        {unreadCount > 0 && (
          <Badge
            value={unreadCount > 99 ? '99+' : unreadCount}
            severity="danger"
            className="absolute -top-0.5 -right-0.5"
            style={{ fontSize: '0.6rem', minWidth: '1.05rem', height: '1.05rem', lineHeight: '1.05rem' }}
          />
        )}
      </button>

      <OverlayPanel
        ref={op}
        showCloseIcon
        className="w-[480px] max-h-[640px] shadow-xl"
        pt={{
          content: { className: 'p-0' }
        }}
      >
        {/* Header */}
        <div className="p-4 border-b border-line bg-gradient-to-r from-blue-50 to-blue-25 dark:from-blue-900/20 dark:to-transparent">
          <div className="flex justify-between items-center">
            <div className="flex gap-2.5 items-center">
              <div className="w-[30px] h-[30px] rounded-lg bg-blue-500 flex items-center justify-center">
                <Icon name="bell" size={16} className="text-white" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-ink">Notifications</h3>
                {unreadCount > 0 && (
                  <p className="text-xs text-ink-muted">
                    {unreadCount} unread {unreadCount === 1 ? 'notification' : 'notifications'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button aria-label="Refresh"
                type="button"
                onClick={fetchNotifications}
                disabled={loading}
                className="p-2 bg-surface hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <Icon name="refresh" size={16} className={loading ? 'animate-spin' : ''} />
              </button>
              {unreadCount > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleMarkAllAsRead}
                  disabled={actionLoading}
                >
                  <Icon name="check" size={16} className="mr-1" />
                  Mark all read
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="m-4">
            <Message severity="error" text={error} className="w-full" />
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex justify-center items-center py-16">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} />
          </div>
        )}

        {/* Empty state */}
        {!loading && notifications.length === 0 && (
          <div className="py-20 px-8 text-center flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <Icon name="bell" size={32} className="text-gray-400 opacity-40" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-ink mb-1">
                No notifications yet
              </h4>
              <p className="text-sm text-ink-muted">
                You're all caught up! Check back later for updates.
              </p>
            </div>
          </div>
        )}

        {/* Notification list */}
        {!loading && notifications.length > 0 && (
          <div className="max-h-[500px] overflow-y-auto py-2">
            {notifications.map((notification, index) => (
              <div key={notification._id || index}>
                <NotificationItem
                  notification={notification}
                  onActionClick={handleActionClick}
                  onMarkAsRead={handleMarkAsRead}
                />
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {!loading && notifications.length > 0 && (
          <>
            <Divider className="my-0" />
            <div className="p-3 text-center">
              {/* The engine inbox. `/rtgs/notifications` was the legacy page, which
                  no longer has a route, and `/rtgs` is not this app's base — so a
                  full reload onto it could only ever render NotFound. */}
              <a
                href="/notification-engine?tab=inbox"
                className="text-sm text-blue-600 dark:text-blue-400 font-medium hover:underline cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  op.current?.hide();
                  navigate('/notification-engine?tab=inbox');
                }}
              >
                View all notifications
              </a>
            </div>
          </>
        )}
      </OverlayPanel>

      {/* Memo Dialog */}
      <MemoDialog
        open={memoDialogOpen}
        notification={selectedNotification}
        action={selectedAction}
        onConfirm={handleMemoConfirm}
        onCancel={handleMemoCancel}
        loading={actionLoading}
      />
    </>
  );
};

export default NotificationBell;
